import { useState, useEffect } from "react";
import axios from "axios";
import { X, ExternalLink, Loader2, RefreshCw, CheckCircle2, Share2, Copy, Check } from "lucide-react";
import DOMPurify from "dompurify";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function renderMarkdown(text) {
  if (!text) return "";
  return text
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\n\n/g, "</p><p>")
    .replace(/\n/g, "<br/>");
}

export default function PartyDetailModal({ party, onClose }) {
  const [aiContent, setAiContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [cached, setCached] = useState(false);
  const [error, setError] = useState(null);
  const [displayedContent, setDisplayedContent] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    fetchAIContent();
    return () => { document.body.style.overflow = ""; };
  }, [party.id]);

  // Typewriter effect
  useEffect(() => {
    if (!aiContent) return;
    let i = 0;
    setDisplayedContent("");
    const interval = setInterval(() => {
      if (i < aiContent.length) {
        setDisplayedContent(aiContent.slice(0, i + 1));
        i++;
      } else {
        clearInterval(interval);
      }
    }, 8);
    return () => clearInterval(interval);
  }, [aiContent]);

  const fetchAIContent = async () => {
    setLoading(true);
    setError(null);
    setAiContent("");
    setDisplayedContent("");
    try {
      const res = await axios.post(`${API}/ai/party-content`, {
        party_id: party.id,
        party_name: party.name,
        party_description: party.description,
        content_type: "detail",
      });
      setAiContent(res.data.content);
      setCached(res.data.cached);
    } catch (e) {
      setError("Failed to load AI analysis. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const shareOnX = () => {
    const text = `Learn about the ${party.name} on AusVote — Australia's AI-powered election guide!`;
    const url = window.location.origin;
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text + " " + url)}`,
      "_blank"
    );
  };

  const shareOnWhatsApp = () => {
    const text = `Check out the ${party.name} profile on AusVote — Australia's election guide: ${window.location.origin}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin} — Check out the ${party.name} profile on AusVote!`
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
      data-testid="party-detail-modal"
    >
      <div
        className="w-full max-w-4xl max-h-[90vh] bg-white border-2 border-black flex flex-col"
        style={{ boxShadow: "8px 8px 0px 0px #000000" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          className="flex items-center justify-between p-5 border-b-2 border-black"
          style={{ backgroundColor: party.color }}
        >
          <div className="flex items-center gap-4">
            <div className="text-4xl font-black text-white" style={{ fontFamily: "Outfit, sans-serif", textShadow: "2px 2px 0 rgba(0,0,0,0.3)" }}>
              {party.abbreviation}
            </div>
            <div>
              <h2 className="text-white font-black text-lg leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                {party.name}
              </h2>
              <p className="text-white text-sm opacity-90">{party.ideology} · Founded {party.founded}</p>
            </div>
          </div>
          <button
            data-testid="close-modal-btn"
            onClick={onClose}
            className="p-2 bg-white border-2 border-black hover:bg-gray-100 transition-colors"
            style={{ boxShadow: "2px 2px 0px 0px #000000" }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 divide-y-2 md:divide-y-0 md:divide-x-2 divide-black">

            {/* Left: Party Info */}
            <div className="p-6 space-y-5">
              <div>
                <h3 className="font-black text-sm uppercase tracking-wider mb-2" style={{ color: "#475569" }}>
                  Current Leader
                </h3>
                <p className="font-bold text-[#0F172A]">{party.leader}</p>
              </div>

              <div>
                <h3 className="font-black text-sm uppercase tracking-wider mb-2" style={{ color: "#475569" }}>
                  Key Policies
                </h3>
                <ul className="space-y-2">
                  {party.positions.map((pos, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span
                        className="mt-0.5 w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: party.color }}
                      />
                      <span className="text-sm text-[#0F172A] font-medium">{pos}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="font-black text-sm uppercase tracking-wider mb-2" style={{ color: "#475569" }}>
                  Ideology
                </h3>
                <span
                  className="inline-block px-3 py-1 text-sm font-bold border-2 border-black"
                  style={{ backgroundColor: `${party.color}20`, color: party.color }}
                >
                  {party.ideology}
                </span>
              </div>

              <div className="flex flex-wrap gap-2">
                <a
                  href={party.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`party-website-${party.id}`}
                  className="flex items-center gap-2 text-sm font-bold border-2 border-black px-4 py-2 hover:opacity-80 transition-opacity"
                  style={{ backgroundColor: party.color, color: "#fff", boxShadow: "2px 2px 0px 0px #000000" }}
                >
                  <ExternalLink className="w-4 h-4" />
                  Official Website
                </a>
                {party.donation_url && (
                  <a
                    href={party.donation_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`party-donate-${party.id}`}
                    className="flex items-center gap-2 text-sm font-bold border-2 border-black px-4 py-2 hover:-translate-y-0.5 transition-transform"
                    style={{ backgroundColor: "#FFC82E", color: "#000", boxShadow: "2px 2px 0px 0px #000000" }}
                  >
                    Donate
                  </a>
                )}
              </div>
            </div>

            {/* Right: AI Analysis */}
            <div className="col-span-2 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full animate-pulse"
                    style={{ backgroundColor: loading ? "#FFC82E" : "#10B981" }}
                  />
                  <h3 className="font-black text-sm uppercase tracking-wider" style={{ color: "#475569" }}>
                    AI Political Analysis
                  </h3>
                  {cached && !loading && (
                    <span className="flex items-center gap-1 text-xs text-[#10B981] font-medium">
                      <CheckCircle2 className="w-3 h-3" /> Cached
                    </span>
                  )}
                </div>
                <button
                  data-testid="refresh-ai-content"
                  onClick={fetchAIContent}
                  disabled={loading}
                  className="p-1.5 border-2 border-black hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                </button>
              </div>

              {loading && (
                <div className="flex flex-col items-center justify-center py-12 gap-3" data-testid="ai-loading">
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: party.color }} />
                  <p className="text-sm text-[#475569] font-medium">Generating AI analysis...</p>
                  <p className="text-xs text-[#475569]">Powered by GPT-4o</p>
                </div>
              )}

              {error && !loading && (
                <div
                  className="p-4 border-2 border-[#C04E3F] bg-red-50 text-[#C04E3F] text-sm font-medium"
                  data-testid="ai-error"
                >
                  {error}
                  <button
                    onClick={fetchAIContent}
                    className="ml-3 underline font-bold hover:no-underline"
                  >
                    Retry
                  </button>
                </div>
              )}

              {!loading && !error && displayedContent && (
                <div
                  data-testid="ai-content"
                  className={`text-sm text-[#0F172A] leading-relaxed space-y-3 typewriter-cursor`}
                  style={{ fontFamily: "Public Sans, sans-serif" }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(`<p>${renderMarkdown(displayedContent)}</p>`),
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t-2 border-black bg-[#F9F7F2] flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-[#475569] max-w-xs">
            AI analysis powered by GPT-4o. For civic education only. Verify with official sources.
          </p>
          <div className="flex items-center gap-2">
            <button
              data-testid="share-whatsapp-btn"
              onClick={shareOnWhatsApp}
              title="Share on WhatsApp"
              className="flex items-center gap-1.5 px-3 py-2 font-bold text-xs border-2 border-black hover:-translate-y-0.5 transition-transform"
              style={{ backgroundColor: "#25D366", color: "#fff", boxShadow: "2px 2px 0px 0px #000" }}
            >
              WhatsApp
            </button>
            <button
              data-testid="share-x-btn"
              onClick={shareOnX}
              title="Share on X"
              className="flex items-center gap-1.5 px-3 py-2 text-white font-bold text-xs border-2 border-black hover:-translate-y-0.5 transition-transform"
              style={{ backgroundColor: "#0F172A", boxShadow: "2px 2px 0px 0px #000" }}
            >
              <Share2 className="w-3.5 h-3.5" /> Share on X
            </button>
            <button
              data-testid="copy-link-btn"
              onClick={copyLink}
              title="Copy link"
              className="flex items-center gap-1.5 px-3 py-2 font-bold text-xs border-2 border-black hover:-translate-y-0.5 transition-transform"
              style={{ backgroundColor: copied ? "#10B981" : "#FFC82E", color: copied ? "#fff" : "#000", boxShadow: "2px 2px 0px 0px #000" }}
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copied!" : "Copy Link"}
            </button>
            <button
              data-testid="close-modal-footer-btn"
              onClick={onClose}
              className="px-4 py-2 font-bold text-sm border-2 border-black hover:bg-[#FFC82E] transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
